import {ApplicationMarketState} from '@/components/ApplicationMarketState'
import {FeatureToggle} from '@/config/featureToggles'
import {isFeatureEnabled} from '@/helpers/featureToggle'
import {useApplications} from '@/hooks/useApplications'
import {useAuth} from '@/hooks/useAuth'
import {PageError403} from '@/pages/Errors/403'
import {PageError500} from '@/pages/Errors/500'
import SessionExpired from '@/pages/Errors/SessionExpired'
import {Logout} from '@/pages/Logout'
import MessageTemplates from '@/pages/MessageTemplates'
import {useAppDispatch} from '@/store'
import {setApplication, setMarket, setShellActive, setShellPath} from '@/store/slices/commonSlice'
import {setDarkMode} from '@/store/slices/themeSlice'
import {useEffect} from 'react'
import {Route, Routes} from 'react-router-dom'
import {ROUTES} from '../../config/constants'
import {AdminRoute} from '../../helpers/AdminRoute'
import {ProtectedRoutes} from '../../helpers/ProtectedRoutes'
import {Home, Login, PageError404} from '../../pages'
import {JourneyEditor} from '../../pages/JourneyEditor'
import {MarketsPage} from '../../pages/MarketsPage'
import {SettingsPage} from '../../pages/SettingsPage'
import TriggerBasedNotification from '../../pages/TriggerBasedNotification'

interface AppRouterProps {
  shellActive: boolean
  darkTheme: boolean
  path: string
  market: string
}

export const AppRouter = ({shellActive, darkTheme, path, market}: AppRouterProps) => {
  const dispatch = useAppDispatch()
  const {isAuthenticated} = useAuth()
  const {
    applications,
    allApplications,
    isLoading: isApplicationsLoading,
    isFetching: isApplicationsFetching,
    isError: isApplicationsError,
    refetch: refetchApplications,
  } = useApplications({enabled: isAuthenticated})

  useEffect(() => {
    dispatch(setShellActive(shellActive))
    if (shellActive) {
      dispatch(setApplication('B2B'))
      dispatch(setMarket(market))
    }
  }, [market, shellActive, dispatch])

  useEffect(() => {
    if (shellActive || !applications.length) return

    const selectedApplication = applications[0]
    dispatch(setApplication(selectedApplication.code))
  }, [applications, shellActive, dispatch])

  useEffect(() => {
    dispatch(setDarkMode(darkTheme))
    document.documentElement.classList.toggle('dark', darkTheme)
  }, [darkTheme, dispatch])

  // expected everytime to dispatch the shell path when component is mounted to navigate properly
  useEffect(() => {
    dispatch(setShellPath(path))
  })

  const catalogState = (
    <ApplicationMarketState
      isLoading={isApplicationsLoading}
      isFetching={isApplicationsFetching}
      isError={isApplicationsError}
      hasServerApplications={allApplications.length > 0}
      hasAccessibleApplications={applications.length > 0}
      onRetry={() => {
        void refetchApplications()
      }}
    />
  )

  const protectedRoutes =
    isAuthenticated &&
    (isApplicationsLoading ||
      (isApplicationsFetching && applications.length === 0) ||
      isApplicationsError ||
      allApplications.length === 0 ||
      applications.length === 0) ? (
      catalogState
    ) : (
      <ProtectedRoutes />
    )

  return (
    <Routes>
      <Route element={protectedRoutes}>
        {/* Features */}
        <Route path={ROUTES.HOME} element={<Home />} />
        <Route path={ROUTES.ROOT} element={<Home />} />
        <Route path={ROUTES.LIST_JOURNEYS} element={<Home />} />
        <Route path={ROUTES.CREATE_JOURNEY} element={<JourneyEditor />} />
        <Route path={ROUTES.EDIT_JOURNEY} element={<JourneyEditor />} />
        <Route path={ROUTES.LOGOUT} element={<Logout />} />
        <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />
        {isFeatureEnabled(FeatureToggle.marketsPage) && (
          <Route element={<AdminRoute />}>
            <Route path={ROUTES.MARKETS} element={<MarketsPage />} />
          </Route>
        )}
        {isFeatureEnabled(FeatureToggle.realTimeNotificationPage) && (
          <>
            <Route path={ROUTES.EVENT_NOTIFICATIONS} element={<TriggerBasedNotification />} />
            <Route path={ROUTES.EVENT_NOTIFICATION} element={<TriggerBasedNotification />} />
            {isFeatureEnabled(FeatureToggle.realTimeNotificationEditDetails) && (
              <Route path={ROUTES.EVENT_NOTIFICATION_EDIT} element={<TriggerBasedNotification />} />
            )}
          </>
        )}
        {isFeatureEnabled(FeatureToggle.messageTemplates) && (
          <>
            <Route path={ROUTES.MESSAGE_TEMPLATES} element={<MessageTemplates />} />
            <Route path={ROUTES.MESSAGE_TEMPLATE} element={<MessageTemplates />} />
            <Route path={ROUTES.MESSAGE_TEMPLATE_EDIT} element={<MessageTemplates />} />
          </>
        )}
      </Route>
      {/* Auth */}
      <Route
        path={ROUTES.LOGIN}
        element={<Login />}
        // element={<LoginCallback errorComponent={PageError403} />}
      />

      {/* Errors */}
      <Route path={ROUTES.ERROR_500} element={<PageError500 />} />
      <Route path={ROUTES.ERROR_403} element={<PageError403 />} />
      <Route path="*" element={<PageError404 />} />
      <Route path={ROUTES.SESSION_EXPIRED} element={<SessionExpired />} />
    </Routes>
  )
}
